import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { toast } from "sonner";

const EMPTY = { name: "", phone: "", email: "", address: "", suburb: "", postcode: "", preferred_channel: "sms", business_name: "", abn: "" };
const L = ({ children }) => <Label className="label-caps text-muted-foreground">{children}</Label>;

export default function QuickClientDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.name.trim()) return toast.error("Client name is required");
    setSaving(true);
    try {
      const { data } = await api.post("/customers", { ...form, name: form.name.trim(), notes: "", sms_opt_out: false });
      toast.success(`${data.name} added — now finish the booking`);
      onCreated(data);
      setForm(EMPTY);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="quick-client-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">New Client</DialogTitle>
          <DialogDescription>Add the client, then you'll return to the booking with them selected.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2"><L>Name *</L><Input value={form.name} onChange={set("name")} autoFocus data-testid="quick-client-name" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><L>Mobile</L><Input value={form.phone} onChange={set("phone")} placeholder="0412 345 678" data-testid="quick-client-phone" /></div>
            <div className="space-y-2"><L>Email</L><Input type="email" value={form.email} onChange={set("email")} data-testid="quick-client-email" /></div>
          </div>
          <div className="space-y-2">
            <L>Address</L>
            <AddressAutocomplete value={form.address} testid="quick-client-address"
              onChange={(v) => setForm((f) => ({ ...f, address: v }))}
              onSelect={(p) => setForm((f) => ({ ...f, address: p.address || f.address, suburb: p.suburb || f.suburb, postcode: p.postcode || f.postcode }))} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><L>Suburb</L><Input value={form.suburb} onChange={set("suburb")} data-testid="quick-client-suburb" /></div>
            <div className="space-y-2"><L>Postcode</L><Input value={form.postcode} onChange={set("postcode")} data-testid="quick-client-postcode" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><L>Business name</L><Input value={form.business_name} onChange={set("business_name")} placeholder="Optional" data-testid="quick-client-business" /></div>
            <div className="space-y-2"><L>ABN</L><Input value={form.abn} onChange={set("abn")} placeholder="Optional" data-testid="quick-client-abn" /></div>
          </div>
          <div className="space-y-2">
            <L>Reminder channel</L>
            <select value={form.preferred_channel} onChange={set("preferred_channel")} data-testid="quick-client-channel"
              className="w-full border border-border/70 rounded-sm px-3 py-2 bg-background text-sm outline-none focus:border-brand">
              <option value="both">SMS &amp; Email</option>
              <option value="sms">SMS only</option>
              <option value="email">Email only</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="quick-client-cancel" className="rounded-sm">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="quick-client-save" className="rounded-sm">{saving ? "Saving…" : "Add client & continue"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
